import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCurrentTerms, fetchLegalDocuments, publishLegalDocument } from "@/api/legal";

const key = ["system", "legal-documents"];

/** Every published version of the Platform Terms, newest upload first. */
export function useLegalDocuments() {
  return useQuery({ queryKey: key, queryFn: () => fetchLegalDocuments("platform_terms") });
}

/**
 * Publish a new version.
 *
 * Invalidates the list and the current-version query: an upload dated today changes what signup
 * serves immediately, so a screen still showing the previous version would be wrong.
 */
export function usePublishLegalDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishLegalDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({ queryKey: ["legal", "current"] });
    },
  });
}

/**
 * The version the signup clickwrap names.
 *
 * `staleTime: Infinity` — the current version changes when someone publishes, which is not
 * something a half-finished signup should see happen underneath it. The server checks the version
 * again at onboard and refuses a stale one, so this cache cannot let a wrong acceptance through.
 */
export function useCurrentTerms() {
  return useQuery({
    queryKey: ["legal", "current", "platform_terms"],
    queryFn: () => fetchCurrentTerms("platform_terms"),
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}
